import React, {
  useState, useContext, useMemo, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { Box, IconButton, Collapse } from '@mui/material';
import Typography from '@digital-hig/mui/dist/Typography';
import Button from '@mui/material/Button';
import Card from '@digital-hig/mui/dist/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import SvgIcon from '@mui/material/SvgIcon';
import styled from 'styled-components';
import SvgClock from '@digital-hig/icon/lib/esm/icons/clock';
import SvgLocation from '@digital-hig/icon/lib/esm/icons/location';
import SvgCalendar from '@digital-hig/icon/lib/esm/icons/calendar';
import SvgCaretDown from '@digital-hig/icon/lib/esm/icons/caret-down';
import SvgCaretUp from '@digital-hig/icon/lib/esm/icons/caret-up';
import {
  formatDateTime, hasValidStartDateTime, getContentItemStatus, getStatusDisplayText, CONTENT_ITEM_STATUS,
  generateICSContent, downloadICSFile,
} from '../common/fastTrackUtils';
import NumberedCircle from './numberedCircle';
import Chip from '../common/chip';
import EnvContext from '../common/envContext';
import { ENVIRONMENT } from '../common/constants';
import { triggerManualLinkClick } from '../common/analyticsApi';

// Business configurable thresholds for Zoom button visibility (in minutes)
const ZOOM_BUTTON_CONFIG = {
  SHOW_BEFORE_START_MINUTES: 360, // Show button 6 hours before class starts
  SHOW_AFTER_END_MINUTES: 10, // Keep button available 10 minutes after class ends
};

// Styled CardContent with completed state styling
const StyledCardContent = styled(CardContent)`
  &.completed {
    background-color: #f9f9f9;
    border: 1px solid #e5e5e5;
  }
`;

// Helper function to process description text with escape characters
const processDescription = (description) => {
  if (!description) return '';

  // Process escape characters (both literal and escaped)
  const processed = description
    .replace(/\\n/g, '\n') // Convert escaped \n to actual newlines
    .replace(/\\t/g, '\t') // Convert escaped \t to actual tabs
    .replace(/\\r/g, '\r') // Convert escaped \r to actual carriage returns
    .replace(/\\\\/g, '\\'); // Convert \\ to single backslash

  return processed;
};

// Helper component to render description with proper line breaks
const DescriptionRenderer = ({ description }) => {
  const processedDescription = processDescription(description);

  if (!processedDescription) return null;

  // Split by newlines and render each line as a separate element
  const lines = processedDescription.split(/\n/);

  return (
    <>
      {lines.map((line, index) => {
        // Create a more stable key using line content hash
        const lineKey = `line-${line.length}-${line.replace(/\s/g, '').substring(0, 5)}-${index}`;
        return (
          <React.Fragment key={lineKey}>
            {line}
            {index < lines.length - 1 && <br />}
          </React.Fragment>
        );
      })}
    </>
  );
};

DescriptionRenderer.propTypes = {
  description: PropTypes.string,
};

DescriptionRenderer.defaultProps = {
  description: '',
};

const LearnLiveClassCard = ({
  data = {},
  learningRecords = {},
  analyticsArea = 'learningplan',
  className = '',
  clickEvent,
  itemIndex = 0,
}) => {
  const [showDescription, setShowDescription] = useState(false);
  const [calendarError, setCalendarError] = useState('');
  const envContext = useContext(EnvContext);
  const env = envContext[ENVIRONMENT];

  // Extract data from the content item
  const {
    title = '',
    startDatetime,
    endDatetime,
    stillOffered = true,
    meetingPlatform = '',
    meetingLink,
    parentCourse,
    attended = undefined,
  } = data;

  // Get description from parent course overview
  const description = parentCourse?.overview || '';

  // Memoize expensive calculations to prevent unnecessary recalculations on re-render
  const status = useMemo(() => getContentItemStatus(data, learningRecords, env, attended),
    [data, learningRecords, env, attended]);

  const statusDisplayText = useMemo(() => getStatusDisplayText(status),
    [status]);

  const hasValidDateTime = useMemo(() => hasValidStartDateTime(data),
    [data]);

  const isCompleted = useMemo(() => status === CONTENT_ITEM_STATUS.ATTENDED || status === CONTENT_ITEM_STATUS.NOT_ATTENDED,
    [status]);

  const orderNumber = itemIndex + 1; // Convert 0-based index to 1-based order number

  // Memoize Zoom button visibility calculation
  const isZoomButtonVisible = useMemo(() => {
    if (!hasValidDateTime || !startDatetime) {
      return false; // Don't show if no valid datetime
    }

    const now = Date.now();
    const startTime = new Date(startDatetime).getTime();
    const endTime = endDatetime ? new Date(endDatetime).getTime() : startTime + (60 * 60 * 1000); // Default to 1 hour if no end time

    const showFromTime = startTime - (ZOOM_BUTTON_CONFIG.SHOW_BEFORE_START_MINUTES * 60 * 1000);
    const showUntilTime = endTime + (ZOOM_BUTTON_CONFIG.SHOW_AFTER_END_MINUTES * 60 * 1000);

    return now >= showFromTime && now <= showUntilTime;
  }, [hasValidDateTime, startDatetime, endDatetime]);

  // Memoize Add to Calendar button visibility calculation
  const isAddToCalendarButtonVisible = useMemo(() => {
    if (!hasValidDateTime || !startDatetime) {
      return false; // Don't show if no valid datetime
    }

    const now = Date.now();
    const startTime = new Date(startDatetime).getTime();

    // Show if class is in the future and still offered
    return startTime > now && stillOffered;
  }, [hasValidDateTime, startDatetime, stillOffered]);

  // Memoize formatted date and time calculation
  const formattedDateTime = useMemo(() => (hasValidDateTime
    ? formatDateTime(startDatetime, endDatetime)
    : 'Date TBD'),
  [hasValidDateTime, startDatetime, endDatetime]);

  // Helper to validate link before opening
  const isValidLink = (href) => Boolean(href) && href !== '#';

  // Memoized handler for joining meeting
  const handleJoinMeeting = useCallback(() => {
    // Trigger analytics tracking
    const platform = (meetingPlatform && meetingPlatform.trim()) ? meetingPlatform.toLowerCase() : 'meeting';
    triggerManualLinkClick({
      linkTitle: `my-dashboard:learning-plan:fast-track:join-${platform}`,
      linkSection: 'content',
    });

    if (clickEvent) {
      clickEvent({
        href: meetingLink,
        data,
        analyticsArea,
        buttonName: 'join-meeting',
        learningRecords,
        env,
      });
    }
    if (isValidLink(meetingLink)) {
      window.open(meetingLink, '_blank');
    }
  }, [clickEvent, meetingLink, data, analyticsArea, learningRecords, env, meetingPlatform]);

  // Memoized handler for adding to calendar (ICS download)
  const handleAddToCalendar = useCallback(() => {
    // Clear any previous errors
    setCalendarError('');

    try {
      // Check if class is cancelled - don't allow adding to calendar
      if (stillOffered === false) {
        throw new Error('Cannot add cancelled class to calendar');
      }

      // Check if we have valid date/time information
      if (!hasValidStartDateTime(data)) {
        throw new Error('Invalid class date/time information');
      }

      // Trigger analytics tracking
      triggerManualLinkClick({
        linkTitle: 'my-dashboard:learning-plan:fast-track:add-to-calendar',
        linkSection: 'content',
      });

      // Generate ICS content
      const icsContent = generateICSContent({
        id: data.id,
        title: data.title,
        startDatetime: data.startDatetime,
        endDatetime: data.endDatetime,
        meetingPlatform: data.meetingPlatform,
        meetingLink: data.meetingLink,
        customerEntitlement: data.customerEntitlement,
        parentCourse: data.parentCourse,
        instructor: data.instructor,
      });

      // Create filename with class title and date
      const startDate = new Date(data.startDatetime);
      const dateStr = startDate.toISOString().split('T')[0]; // YYYY-MM-DD format
      const safeTitle = data.title.replace(/[^a-zA-Z0-9\s-]/g, '').replace(/\s+/g, '-');
      const filename = `${safeTitle}-${dateStr}.ics`;

      // Download the ICS file
      downloadICSFile(icsContent, filename);

      // Call analytics if provided
      if (clickEvent) {
        clickEvent({
          href: '#',
          data,
          analyticsArea,
          buttonName: 'add-to-calendar',
          learningRecords,
          env,
        });
      }
    } catch (error) {
      setCalendarError(error.message);
    }
  }, [stillOffered, data, clickEvent, analyticsArea, learningRecords, env]);

  const toggleDescription = () => {
    setShowDescription(!showDescription);
  };

  return (
    <Box className={`learnLiveClassCard ${className}`} sx={{ width: '100%', marginBottom: 2 }}>
      <Card>
        <StyledCardContent className={isCompleted ? 'completed' : ''} sx={{ padding: '20px 24px 20px 20px' }}>
          {/* Header with number, title, and status */}
          <Box sx={{
            display: 'flex', alignItems: 'flex-start',
          }}
          >
            <NumberedCircle
              number={orderNumber}
              isCompleted={isCompleted}
              sx={{ marginRight: '1rem' }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="headline-smaller"
                component="h3"
                sx={{
                  fontWeight: 600, mb: 5, lineHeight: 1.3,
                }}
              >
                {title}
              </Typography>
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
              }}
              >
                <Chip value={{ id: status, label: statusDisplayText }} />
                <Typography variant="short-copy-small" component="span" sx={{ color: '#666' }}>
                  Coaching session
                </Typography>
              </Box>

              {/* Date/Time and Location */}
              <Box sx={{
                display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 1, md: 3 }, my: 6,
              }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SvgIcon sx={{ fontSize: 18, color: '#666' }}>
                    <SvgClock />
                  </SvgIcon>
                  <Typography variant="body-medium" component="span">
                    {formattedDateTime}
                  </Typography>
                </Box>
                {meetingPlatform && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SvgIcon sx={{ fontSize: 18, color: '#666' }}>
                      <SvgLocation />
                    </SvgIcon>
                    <Typography variant="body-medium" component="span">
                      {`${meetingPlatform} meeting`}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Description section */}
              {description && (
                <Box sx={{ marginBottom: 2 }}>
                  <IconButton
                    data-testid="toggle-description"
                    onClick={toggleDescription}
                    sx={{
                      padding: 0,
                      color: '#000',
                      textDecoration: 'none',
                    }}
                  >
                    <Typography variant="headline-smaller" component="span">
                      See description
                    </Typography>
                    <SvgIcon sx={{ fontSize: 16, marginLeft: 1 }}>
                      {showDescription ? <SvgCaretUp /> : <SvgCaretDown />}
                    </SvgIcon>
                  </IconButton>
                  {showDescription && (
                    <Collapse in={showDescription}>
                      <Box>
                        <Typography sx={{ my: 3 }}>
                          <DescriptionRenderer description={description} />
                        </Typography>
                      </Box>
                    </Collapse>
                  )}
                </Box>
              )}

              { (isZoomButtonVisible || isAddToCalendarButtonVisible) && (
                <>
                  {/* Divider */}
                  <Divider sx={{ my: 2 }} />
                  {/* Action buttons */}
                  <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
                    {isZoomButtonVisible && (
                      <Button
                        variant="contained"
                        onClick={handleJoinMeeting}
                        disabled={!isValidLink(meetingLink)}
                      >
                        Join ZOOM meeting
                      </Button>
                    )}
                    {isAddToCalendarButtonVisible && (
                      <Button
                        variant="text"
                        startIcon={(
                          <SvgIcon>
                            <SvgCalendar />
                          </SvgIcon>
                        )}
                        onClick={handleAddToCalendar}
                        disabled={stillOffered === false || !hasValidStartDateTime(data)}
                      >
                        Add to calendar
                      </Button>
                    )}
                    {calendarError && (
                      <Typography variant="error" sx={{ mt: 1, fontSize: '0.875rem' }}>
                        {calendarError}
                      </Typography>
                    )}
                  </Box>
                </>
              )}
            </Box>
          </Box>
        </StyledCardContent>

      </Card>
    </Box>
  );
};

LearnLiveClassCard.propTypes = {
  data: PropTypes.shape({
    id: PropTypes.string.isRequired,
    isRemoved: PropTypes.bool,
    source: PropTypes.string,
    type: PropTypes.string,
    title: PropTypes.string.isRequired,
    stillOffered: PropTypes.bool,
    localeOfClass: PropTypes.string,
    customerEntitlement: PropTypes.arrayOf(PropTypes.string),
    startDatetime: PropTypes.string,
    endDatetime: PropTypes.string,
    meetingPlatform: PropTypes.string,
    meetingLink: PropTypes.string,
    classType: PropTypes.arrayOf(PropTypes.string),
    clientId: PropTypes.string,
    actionStepId: PropTypes.string,
    parentCourse: PropTypes.shape({
      title: PropTypes.string,
      stillOffered: PropTypes.bool,
      type: PropTypes.string,
      id: PropTypes.string,
      slug: PropTypes.string,
      shortDescription: PropTypes.string,
      overview: PropTypes.string,
      customizationLevel: PropTypes.bool,
      coachingType: PropTypes.arrayOf(PropTypes.string),
      uri: PropTypes.string,
    }),
    instructor: PropTypes.shape({
      type: PropTypes.string,
      id: PropTypes.string,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      email: PropTypes.string,
      bio: PropTypes.string,
      uri: PropTypes.string,
    }),
    uri: PropTypes.string,
  }).isRequired,
  learningRecords: PropTypes.shape({}),
  analyticsArea: PropTypes.string,
  className: PropTypes.string,
  clickEvent: PropTypes.func,
  itemIndex: PropTypes.number,
};

LearnLiveClassCard.defaultProps = {
  learningRecords: {},
  analyticsArea: 'learningplan',
  className: '',
  clickEvent: () => {},
  itemIndex: 0,
};

export default LearnLiveClassCard;
